import type * as React from 'react';

/**
 * Etiqueta de identificação do localizador, 100 × 50 mm — a MESMA do RTM do
 * PBS ("rtm etiquetas.rtm", lido em 28/10/2026), para a etiqueta nova não
 * destoar das que já estão nas ruas e o coletor ler igual:
 *
 *   SELECT DBO.ZEROS(LOCALIZADOR, 13) AS CODIGO_BARRAS,
 *          DESCRICAO AS LOCALIZADOR,
 *          CASE WHEN TIPO_LOCALIZADOR = 1 THEN 'Dinâmico' ELSE 'Fixo' END AS TIPO
 *     FROM CADASTROS_LOCALIZADORES
 *
 * Posições e fontes copiadas do RTM, em mm: título Arial 10 negrito no topo,
 * o tipo em Arial 8 logo abaixo, o código em CODE 39 (não 128: é o que o RTM
 * usa), proporção 3:1, sem dígito verificador, com o número embaixo, e a
 * descrição em Arial 36 negrito no pé.
 *
 * Impressa por `imprimirEtiquetas` — o mesmo caminho da etiqueta de separação
 * e da alocação, com o que já se aprendeu lá: uma `.etiqueta` por página, de
 * 49,9 mm (50 exatos davam folha em branco), quadro que não some enquanto o
 * operador ajusta a obscuridade no driver, e TUDO em `style` inline, porque o
 * documento da impressão não carrega folha de estilo nenhuma. Para a térmica
 * não sair clara: preto puro, barras sem anti-aliasing (`crispEdges`) em
 * múltiplos do ponto de 203 dpi (0,125 mm) e texto em negrito — o Courier New
 * do RTM, fino, some no papel térmico.
 */

/**
 * Code 39 só com o que o código do localizador usa (dígitos) e o `*` de
 * início/fim. Cada caractere: 9 elementos, barra e espaço alternados a começar
 * pela barra; n = estreito, w = largo.
 */
const CODE39: Record<string, string> = {
  '0': 'nnnwwnwnn',
  '1': 'wnnwnnnnw',
  '2': 'nnwwnnnnw',
  '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw',
  '5': 'wnnwwnnnn',
  '6': 'nnwwwnnnn',
  '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn',
  '9': 'nnwwnnwnn',
  '*': 'nwnnwnwnn',
};

/** 0,254 mm no RTM; 0,25 = 2 pontos exatos a 203 dpi, sem barra "meio ponto". */
const ESTREITO_MM = 0.25;
const LARGO_MM = ESTREITO_MM * 3;

function Code39({ texto, alturaMm }: { texto: string; alturaMm: number }): JSX.Element | null {
  if (!/^\d+$/.test(texto)) return null;
  const barras: { x: number; l: number }[] = [];
  let x = 0;
  [...`*${texto}*`].forEach((c, i) => {
    if (i > 0) x += ESTREITO_MM; // espaço entre caracteres
    [...CODE39[c]!].forEach((e, j) => {
      const l = e === 'w' ? LARGO_MM : ESTREITO_MM;
      if (j % 2 === 0) barras.push({ x, l });
      x += l;
    });
  });
  return (
    <svg
      width={`${x.toFixed(3)}mm`}
      height={`${alturaMm}mm`}
      viewBox={`0 0 ${x.toFixed(3)} ${alturaMm}`}
      preserveAspectRatio="none"
      shapeRendering="crispEdges"
      style={{ display: 'block' }}
      role="img"
      aria-label={`Código de barras ${texto}`}
    >
      {barras.map((b) => (
        <rect key={b.x} x={b.x.toFixed(3)} y={0} width={b.l.toFixed(3)} height={alturaMm} fill="#000" />
      ))}
    </svg>
  );
}

/** O código de barras do localizador: o número com 13 dígitos (DBO.ZEROS(LOCALIZADOR, 13)). */
export const codigoDoLocalizador = (localizador: number): string => String(localizador).padStart(13, '0');

export interface DadosEtiquetaLocalizador {
  localizador: number;
  /** CADASTROS_LOCALIZADORES.DESCRICAO — o que vai em letra grande. */
  descricao: string;
  tipo: string;
}

/** Descrição longa encolhe para caber na largura (36 pt cabem ~12 caracteres em 97 mm). */
function tamanhoDaDescricao(texto: string): number {
  return texto.length <= 12 ? 36 : Math.max(18, Math.floor((36 * 12) / texto.length));
}

const ARIAL = 'Arial, Helvetica, sans-serif';
const absoluto = (top: number, left: number, width: number): React.CSSProperties => ({
  position: 'absolute',
  top: `${top}mm`,
  left: `${left}mm`,
  width: `${width}mm`,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  color: '#000',
});

export function EtiquetaLocalizador({ dados }: { dados: DadosEtiquetaLocalizador }): JSX.Element {
  const codigo = codigoDoLocalizador(dados.localizador);
  const descricao = dados.descricao || String(dados.localizador);
  return (
    <div className="etiqueta" style={{ position: 'relative', width: '100mm', height: '49.9mm', overflow: 'hidden', background: '#fff' }}>
      <div style={{ ...absoluto(3.2, 8.5, 83), font: `bold 10pt ${ARIAL}` }}>Etiqueta de Identificação do Localizador</div>
      <div style={{ ...absoluto(8.5, 0, 100), font: `bold 8pt ${ARIAL}` }}>{dados.tipo}</div>
      <div style={{ ...absoluto(12.7, 17.5, 65), display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Code39 texto={codigo} alturaMm={11.5} />
        <div style={{ font: 'bold 12pt "Courier New", monospace', lineHeight: 1, marginTop: '0.6mm' }}>{codigo}</div>
      </div>
      <div
        style={{
          ...absoluto(29.1, 1.3, 97.4),
          height: '14.3mm',
          lineHeight: '14.3mm',
          fontFamily: ARIAL,
          fontWeight: 'bold',
          fontSize: `${tamanhoDaDescricao(descricao)}pt`,
        }}
      >
        {descricao}
      </div>
    </div>
  );
}
